// Path: ./src/app/components/CreateTagModal/index.tsx
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useState } from "react";
import {
  Card,
  InheritTextInputField,
  StoreTagTItle,
  SubmitButton,
  IconUpload,
  IconInput,
  ImageUploadSection,
  ImagePreviewCard,
  ImagePreview,
  ImagePreviewPlaceholder,
  PreferenceOptionRow,
} from "./styled";
import { SelectWithAddMui } from "../SelectWithAddMui";
import { adminApiService } from "../../../utils/api";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";

import { toastError } from "../../../utils/pageUtil/toast";
import { MuiConfirmationModal } from "../MuiConfirmationModal";
import {
  GfFigmaTemplateData,
  GfFlyerItemCategory,
  GfImage,
  GfImageTag,
  GfMenuTag,
  GfProductTag,
  GfPromotionTag,
  GfStoreTag,
} from "@swagger/typescript-fetch-goflyer";
import CircularProgress from "@mui/material/CircularProgress";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useUploadOrPickImages } from "../../../hooks/useUploadOrPickImages";

import CustomModal from "../CustomModal";
import { disableWheelOnInput } from "../../../utils/disableWheelOnInput";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import ImageDropZone from "../../../components/ImageDropZone";
import { useLoading } from "../../../contextApi/LoadingContext";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { useContext } from "react";

export const TagTypes = {
  Store: "Store",
  Flyer: "Flyer",
  FlyerItem: "Category",
  Menu: "Menu",
  Promotion: "Promotion",
  Product: "Product",
  FigmaTemplate: "FigmaTemplate",
  ImageTag: "ImageTag",
};

export type EditFormType = {
  id: string;
  name: string;
  cnName?: string;
  selectedTag: (typeof TagTypes)[keyof typeof TagTypes] | "";
  index?: number;
  image?: GfImage;
  visible?: boolean;
  description?: string;
  isPreferenceOption?: boolean;
};
interface Props {
  // eslint-disable-next-line no-unused-vars
  onClose: (tagId?: string) => void;
  editForm?: EditFormType;
}
export const CreateTagModal = ({
  onClose,
  editForm = { id: "", name: "", cnName: "", selectedTag: "" },
}: Props) => {
  const path = "tagImages";
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();
  const { showLoadingScreen } = useLoading();
  const { state } = useContext(GoFlyerAdminContext);
  const {
    handleOnImageFileSelect,
    isUploadingImage,
    isDragging,
    getDropZoneProps,
    showReorderModal,
    pendingDropFiles,
    handleReorderConfirm,
    handleReorderCancel,
  } = useUploadOrPickImages({
    onPickedImageChange: async (images: GfImage[]) => {
      setForm((prev) => ({ ...prev, image: images[0] }));
    },
    dragAndDrop: { enabled: true, defaultPath: path },
  });

  const [form, setForm] = useState({
    name: editForm?.name || "",
    cnName: editForm?.cnName || "",
    selectedTag: editForm?.selectedTag || "",
    index: editForm?.index || 0,
    image: editForm?.image || undefined,
    visible: editForm?.visible !== undefined ? editForm?.visible : true,
    description: editForm?.description || "",
    isPreferenceOption: editForm?.isPreferenceOption ?? false,
  });

  const [open, setOpen] = useState(false);

  const tagOptions = [
    { label: t(messages.Store()), value: TagTypes.Store },
    { label: t(messages.Flyer()), value: TagTypes.Flyer },
    { label: t(messages.FlyerItem()), value: TagTypes.FlyerItem },
    { label: t(messages.Menu()), value: TagTypes.Menu },
    { label: t(messages.Promotion()), value: TagTypes.Promotion },
    { label: t(messages.Product()), value: TagTypes.Product },
    { label: t(messages.FigmaTemplate()), value: TagTypes.FigmaTemplate },
    { label: t(messages.ImageTag()), value: TagTypes.ImageTag },
  ];

  const createTagsMethodById: {
    [key: string]: () => Promise<
      | GfStoreTag
      | GfFlyerItemCategory
      | GfMenuTag
      | GfPromotionTag
      | GfProductTag
      | GfFigmaTemplateData
      | GfImageTag
    >;
  } = {
    [`${TagTypes.Store}`]: () =>
      adminApiService.createOneBaseGfStoreTagAdminControllerGfStoreTag({
        name: form.name,
        nameChinese: form.cnName,
        index: 1,
        image: form.image,
        description: form.description,
        adminUser: state.gfAdminUser,
      }),
    [`${TagTypes.Flyer}`]: () =>
      adminApiService.createOneBaseGfFlyerTagAdminControllerGfFlyerTag({
        name: form.name,
        nameChinese: form.cnName,
        index: form.index,
        image: form.image,
        description: form.description,
        visible: form.visible,
        adminUser: state.gfAdminUser,
      }),
    [`${TagTypes.FlyerItem}`]: () =>
      adminApiService.createOneBaseGfFlyerItemCategoryAdminControllerGfFlyerItemCategory(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          isPreferenceOption: form.isPreferenceOption,
          adminUser: state.gfAdminUser,
        },
      ),
    [`${TagTypes.Menu}`]: () =>
      adminApiService.createOneBaseGfMenuTagAdminControllerGfMenuTag({
        name: form.name,
        nameChinese: form.cnName,
        index: 1,
        image: form.image,
        description: form.description,
        adminUser: state.gfAdminUser,
      }),
    [`${TagTypes.Promotion}`]: () =>
      adminApiService.createOneBaseGfPromotionTagAdminControllerGfPromotionTag({
        name: form.name,
        nameChinese: form.cnName,
        index: 1,
        image: form.image,
        description: form.description,
        adminUser: state.gfAdminUser,
      }),
    [`${TagTypes.Product}`]: () =>
      adminApiService.createOneBaseGfProductTagAdminControllerGfProductTag({
        name: form.name,
        nameChinese: form.cnName,
        index: 1,
        image: form.image,
        description: form.description,
        adminUser: state.gfAdminUser,
      }),
    [`${TagTypes.FigmaTemplate}`]: () =>
      adminApiService.createOneBaseGfFigmaTemplateTagAdminControllerGfFigmaTemplateTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          isDeleted: false,
          adminUser: state.gfAdminUser,
        },
      ),
    [`${TagTypes.ImageTag}`]: () =>
      adminApiService.createOneBaseGfImageTagAdminControllerGfImageTag({
        name: form.name,
        nameChinese: form.cnName,
        image: form.image,
        description: form.description,
        adminUser: state.gfAdminUser,
      }),
  };

  const updateTagsMethodById: {
    [key: string]: (
      id: string,
    ) => Promise<
      | GfStoreTag
      | GfFlyerItemCategory
      | GfMenuTag
      | GfPromotionTag
      | GfProductTag
      | GfFigmaTemplateData
      | GfImageTag
    >;
  } = {
    [`${TagTypes.Store}`]: (id: string) =>
      adminApiService.updateOneBaseGfStoreTagAdminControllerGfStoreTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
    [`${TagTypes.Flyer}`]: (id: string) =>
      adminApiService.updateOneBaseGfFlyerTagAdminControllerGfFlyerTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          index: form.index,
          image: form.image,
          description: form.description,
          visible: form.visible,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
    [`${TagTypes.FlyerItem}`]: (id: string) =>
      adminApiService.updateOneBaseGfFlyerItemCategoryAdminControllerGfFlyerItemCategory(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          isPreferenceOption: form.isPreferenceOption,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
    [`${TagTypes.Menu}`]: (id: string) =>
      adminApiService.updateOneBaseGfMenuTagAdminControllerGfMenuTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
    [`${TagTypes.Promotion}`]: (id: string) =>
      adminApiService.updateOneBaseGfPromotionTagAdminControllerGfPromotionTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
    [`${TagTypes.Product}`]: (id: string) =>
      adminApiService.updateOneBaseGfProductTagAdminControllerGfProductTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
    [`${TagTypes.FigmaTemplate}`]: (id: string) =>
      adminApiService.updateOneBaseGfFigmaTemplateTagAdminControllerGfFigmaTemplateTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          isDeleted: false,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
    [`${TagTypes.ImageTag}`]: (id: string) =>
      adminApiService.updateOneBaseGfImageTagAdminControllerGfImageTag(
        {
          name: form.name,
          nameChinese: form.cnName,
          image: form.image,
          description: form.description,
          adminUser: state.gfAdminUser,
        },
        id,
      ),
  };

  const deleteTagsMethodById: {
    [key: string]: (id: string) => Promise<Response>;
  } = {
    [`${TagTypes.Store}`]: (id: string) =>
      adminApiService.deleteOneBaseGfStoreTagAdminControllerGfStoreTag(
        id,
        authHeaders,
      ),
    [`${TagTypes.Flyer}`]: (id: string) =>
      adminApiService.deleteOneBaseGfFlyerTagAdminControllerGfFlyerTag(
        id,
        authHeaders,
      ),
    [`${TagTypes.FlyerItem}`]: (id: string) =>
      adminApiService.deleteOneBaseGfFlyerItemCategoryAdminControllerGfFlyerItemCategory(
        id,
        authHeaders,
      ),
    [`${TagTypes.Menu}`]: (id: string) =>
      adminApiService.deleteOneBaseGfMenuTagAdminControllerGfMenuTag(
        id,
        authHeaders,
      ),
    [`${TagTypes.Promotion}`]: (id: string) =>
      adminApiService.deleteOneBaseGfPromotionTagAdminControllerGfPromotionTag(
        id,
        authHeaders,
      ),
    [`${TagTypes.Product}`]: (id: string) =>
      adminApiService.deleteOneBaseGfProductTagAdminControllerGfProductTag(
        id,
        authHeaders,
      ),
    [`${TagTypes.FigmaTemplate}`]: (id: string) =>
      adminApiService.deleteOneBaseGfFigmaTemplateTagAdminControllerGfFigmaTemplateTag(
        id,
        authHeaders,
      ),
    [`${TagTypes.ImageTag}`]: (id: string) =>
      adminApiService.deleteOneBaseGfImageTagAdminControllerGfImageTag(
        id,
        authHeaders,
      ),
  };
  const checkValidForm = () => {
    const { name, cnName, selectedTag } = form;
    if (!name) {
      toastError(t(messages.Tag_Name_Missing()));
      return false;
    }

    if (!cnName) {
      toastError(t(messages.Tag_CN_Name_Missing()));
      return false;
    }

    if (!selectedTag) {
      toastError(t(messages.Tag_Type_Missing()));
      return false;
    }
    return true;
  };

  const createTag = async (tagType: string) => {
    showLoadingScreen(true);
    await createTagsMethodById[tagType]();
    onClose(tagType); //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) not a good idea, will come here here
    showLoadingScreen(false);
  };

  const updateTag = async (tagType: string, tagId: string) => {
    showLoadingScreen(true);
    await updateTagsMethodById[tagType](tagId);
    onClose(tagType);
    showLoadingScreen(false);
  };

  const onSubmit = async (tagType: string) => {
    const isFormValid = checkValidForm();
    if (isFormValid) {
      if (!editForm?.id) {
        createTag(tagType);

        return;
      }
      editForm?.id && updateTag(tagType, editForm.id);
    }
  };

  const onDelete = async (tagType: string) => {
    editForm?.id && (await deleteTagsMethodById[tagType](editForm.id));
    onClose(tagType);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <CustomModal onClose={onClose} data-testid="create-tag-modal">
      <Card data-testid="create-tag-modal-card">
        <Grid container>
          <Grid
            item
            xs={12}
            style={{
              justifyContent: "center",
              display: "flex",
              marginTop: 12,
            }}
          >
            <InheritTextInputField
              placeholder={"EN Name"}
              onChange={(e) =>
                setForm((prevValue) => ({
                  ...prevValue,
                  name: e.target.value,
                }))
              }
              value={form.name}
              data-testid="create-tag-modal-en-name-input"
            ></InheritTextInputField>
          </Grid>
          <Grid
            item
            xs={12}
            style={{
              justifyContent: "center",
              display: "flex",
              marginTop: 12,
            }}
          >
            <InheritTextInputField
              placeholder={"CN Name"}
              onChange={(e) =>
                setForm((prevValue) => ({
                  ...prevValue,
                  cnName: e.target.value,
                }))
              }
              value={form.cnName}
              data-testid="create-tag-modal-cn-name-input"
            ></InheritTextInputField>
          </Grid>
          {form.selectedTag && (
            <Grid
              item
              xs={12}
              style={{
                justifyContent: "center",
                display: "flex",
                marginTop: 12,
              }}
            >
              <InheritTextInputField
                placeholder={t(messages.Description())}
                onChange={(e) =>
                  setForm((prevValue) => ({
                    ...prevValue,
                    description: e.target.value,
                  }))
                }
                value={form.description}
                data-testid="create-tag-modal-description-input"
              />
            </Grid>
          )}
          {form.selectedTag === TagTypes.Flyer && (
            <>
              <Grid
                item
                xs={12}
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginTop: 12,
                }}
              >
                <InheritTextInputField
                  placeholder="Index"
                  type="number"
                  onWheel={disableWheelOnInput}
                  value={String(form.index)}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      index: Number(e.target.value),
                    }))
                  }
                  data-testid="create-tag-modal-index-input"
                />
              </Grid>
              <Grid
                item
                xs={12}
                style={{
                  justifyContent: "center",
                  display: "flex",
                  marginTop: 12,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.visible}
                      onChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          visible: !prev.visible,
                        }))
                      }
                      data-testid="create-tag-modal-visible-checkbox"
                    />
                  }
                  label="Visible"
                  data-testid="create-tag-modal-visible-toggle"
                />
              </Grid>
            </>
          )}
          {form.selectedTag === TagTypes.FlyerItem && (
            <Grid
              item
              xs={12}
              style={{
                justifyContent: "center",
                display: "flex",
                marginTop: 12,
              }}
            >
              <PreferenceOptionRow>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.isPreferenceOption}
                      onChange={() =>
                        setForm((prev) => ({
                          ...prev,
                          isPreferenceOption: !prev.isPreferenceOption,
                        }))
                      }
                      sx={{
                        color: "#c4c4c4",
                        "&.Mui-checked": { color: "#ff8038" },
                      }}
                      data-testid="create-tag-modal-preference-option-checkbox"
                    />
                  }
                  label={t(messages.preference_category_offered())}
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: 14,
                      fontWeight: 500,
                    },
                  }}
                  data-testid="create-tag-modal-preference-option-toggle"
                />
                <Tooltip
                  title={t(messages.preference_category_offered_tooltip())}
                  placement="top"
                  arrow
                  enterTouchDelay={0}
                >
                  <InfoOutlinedIcon
                    tabIndex={0}
                    aria-label={t(
                      messages.preference_category_offered_tooltip(),
                    )}
                    sx={{
                      fontSize: 17,
                      color: "#9a9a9a",
                      cursor: "help",
                      flex: "none",
                      "&:hover, &:focus-visible": { color: "#ff8038" },
                    }}
                    data-testid="create-tag-modal-preference-option-note"
                  />
                </Tooltip>
              </PreferenceOptionRow>
            </Grid>
          )}
          {form.selectedTag && (
            <Grid
              item
              xs={12}
              style={{
                justifyContent: "center",
                display: "flex",
                marginTop: 12,
              }}
            >
              <Stack
                direction="column"
                spacing={1}
                alignItems="stretch"
                justifyContent={"center"}
                sx={{ width: "100%", maxWidth: 420 }}
              >
                <ImageUploadSection>
                  <ImagePreviewCard data-testid="create-tag-modal-image-preview-card">
                    {form.image?.src ? (
                      <ImagePreview src={form.image.src} alt="tag preview" />
                    ) : (
                      <ImagePreviewPlaceholder>
                        No image selected
                      </ImagePreviewPlaceholder>
                    )}
                  </ImagePreviewCard>

                  <IconUpload>
                    <ImageDropZone
                      uploader={{
                        getDropZoneProps,
                        isDragging,
                        showReorderModal,
                        pendingDropFiles,
                        handleReorderConfirm,
                        handleReorderCancel,
                      }}
                      label={t(messages.Drag_your_image_here())}
                      overlayContent={t(messages.Drag_your_image_here())}
                      overlayStyles={{
                        background: "rgba(0,0,0,0.4)",
                        color: "#fff",
                        fontSize: 14,
                        fontWeight: 600,
                        border: "2px dashed #ff813a",
                      }}
                      style={{ position: "relative", width: "100%" }}
                      data-testid="drag-drop-zone"
                      aria-label="Tag image drop zone"
                    >
                      <>
                        {isUploadingImage && (
                          <CircularProgress
                            data-testid="local-image-loader"
                            sx={{ color: "#ff813a" }}
                            size={32}
                          />
                        )}
                        {!isUploadingImage && (
                          <ImageOutlinedIcon
                            sx={{ fontSize: { xs: "1.8rem", md: "1.8rem" } }}
                          />
                        )}
                        <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                          {t(messages.Drag_your_image_here())}
                        </Typography>
                        <IconInput
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => handleOnImageFileSelect(e, path)}
                          data-testid="image-local-upload"
                        />
                      </>
                    </ImageDropZone>
                  </IconUpload>
                </ImageUploadSection>
              </Stack>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            style={{
              justifyContent: "center",
              display: "flex",
              marginTop: 12,
            }}
          >
            {editForm?.id ? (
              <StoreTagTItle>
                {t(messages.Tag())}: {editForm?.selectedTag} {t(messages.Tag())}
              </StoreTagTItle>
            ) : (
              <SelectWithAddMui
                title={`${t(messages.Select_Tag())}`}
                selectOptions={tagOptions}
                selectedValue={form.selectedTag}
                onSelect={(value) =>
                  setForm((prevValue) => ({
                    ...prevValue,
                    selectedTag:
                      value as (typeof TagTypes)[keyof typeof TagTypes],
                  }))
                }
                wrapperStyle={`
             width:100%;
             display:flex;
             flex-direction: row;
             justify-content:center;
             align-items:center;
              `}
                borderColor={{
                  normal: "#dedede",
                }}
                dataTestId="create-flyer-item-modal-unit-select"
                data-testid="create-tag-modal-type-select"
              ></SelectWithAddMui>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            style={{
              justifyContent: "center",
              display: "flex",
              marginTop: 12,
            }}
            columnGap={2}
          >
            <SubmitButton
              onClick={() => onSubmit(form.selectedTag)}
              data-testid="create-flyer-item-modal-submit-button"
              aria-label="create-tag-modal-submit"
            >
              {editForm?.id ? t(messages.Update()) : t(messages.Submit())}
            </SubmitButton>

            {editForm?.id ? (
              <SubmitButton
                onClick={() => setOpen(true)}
                data-testid="create-flyer-item-modal-delete-button"
                aria-label="create-tag-modal-delete"
              >
                {t(messages.Delete())}
              </SubmitButton>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>
      </Card>

      <MuiConfirmationModal
        open={open}
        handleCancel={handleCancel}
        handleConfirm={() => onDelete(form.selectedTag)}
        title={`${t(messages.Delete_Confirmation())}`}
        description={`
      ${t(messages.Are_you_sure_you_want_to_delete_This())}  ${t(
        messages.Tag(),
      )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
      />
    </CustomModal>
  );
};
